import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    CatalogUerBaseCostEstimateBasisRow,
    CatalogUerBaseCostTotalItem,
    CollectionUerBaseCostEstimateBasisRow,
    CollectionUerBaseCostTotalItem,
} from 'gql/graphql';
import { Tabs } from 'uikit';

import type React from 'react';
import { useCallback, useState, useMemo } from 'react';

import { useUERBaseCostEstimateBasisRowsColumns } from './hooks/useUERBaseCostEstimateBasisRowsColumns';
import type { ICommonUsedProperties } from './hooks/useUERBaseCostEstimateBasisRowsFiltered';
import { useUERBaseCostEstimateBasisRowsFiltered } from './hooks/useUERBaseCostEstimateBasisRowsFiltered';
import { useUERBaseCostEstimateBasisRowsGrouped } from './hooks/useUERBaseCostEstimateBasisRowsGrouped';
import { useUERBaseCostTotalsColumns } from './hooks/useUERBaseCostTotalsColumns';

import Styles from './EstimateBasisModal.module.css';

export type TUseUERBaseCostEstimateBasisRows = (props: Partial<{ withEffect: boolean }>) => {
    status: RequestStatus;
    items: CatalogUerBaseCostEstimateBasisRow[] | CollectionUerBaseCostEstimateBasisRow[];
};

export type TUseUERBaseCostTotals = (props: Partial<{ withEffect: boolean }>) => {
    status: RequestStatus;
    items: CatalogUerBaseCostTotalItem[] | CollectionUerBaseCostTotalItem[];
};

interface IEstimateBasisModal {
    onClose: () => void;
    catalogUniqueId?: string;
    code?: string;
    useUERBaseCostEstimateBasisRows: TUseUERBaseCostEstimateBasisRows;
    useUERBaseCostTotals: TUseUERBaseCostTotals;
    getParams?: Record<string, any>;
}

const tabsItems = [
    {
        id: 'main',
        label: 'Состав сметы',
    },
    {
        id: 'totals',
        label: 'Итоги сметы',
    },
];

export const EstimateBasisModal: React.FC<IEstimateBasisModal> = (props) => {
    const { onClose, code, useUERBaseCostEstimateBasisRows, useUERBaseCostTotals, getParams = {} } = props;

    const title = useMemo(() => `Расценка ${code}`, [code]);

    const [activeTab, setActiveTab] = useState(tabsItems[0]);

    const isMainTabActive = useMemo(() => activeTab === tabsItems[0], [activeTab]);

    const onTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const { status, items } = useUERBaseCostEstimateBasisRows({
        ...getParams,
        withEffect: isMainTabActive,
    });

    const { filterData, filteredData, changeFilterHandler } = useUERBaseCostEstimateBasisRowsFiltered({
        items: items as ICommonUsedProperties[],
    });

    const { data, changeCollapseState, collapseState } = useUERBaseCostEstimateBasisRowsGrouped({
        items: filteredData,
    });

    const { status: totalsStatus, items: totalsItems } = useUERBaseCostTotals({
        ...getParams,
        withEffect: !isMainTabActive,
    });

    const columns = useUERBaseCostEstimateBasisRowsColumns({
        changeCollapseState,
        collapseState,
        changeFilterHandler,
        nameFilterData: filterData?.nameFilterData,
        codeFilterData: filterData?.codeFilterData,
    });

    const totalsColumns = useUERBaseCostTotalsColumns();

    return (
        <ModalDialog
            isOpen
            title={title}
            onClose={onClose}
            dockedToWindow="bottom"
            withTitleCloseButton
            isOnClickOutsideDisable
            isLoading={(isMainTabActive ? status : totalsStatus) === RequestStatus.FETCHING}
            withBottomButtons={false}
        >
            <div className={Styles.modalBody}>
                <Tabs className={Styles.tabs} value={activeTab} onChange={onTabChange} items={tabsItems} size="s" />
                <div className={`${Styles.gridBody} ag-theme-alpine customHeaderColor`}>
                    <AgGridTable
                        rowData={(isMainTabActive ? data : totalsItems) as any}
                        columnDefs={(isMainTabActive ? columns : totalsColumns) as any}
                        overlayNoRowsTemplate="Ничего не найдено"
                    />
                </div>
            </div>
        </ModalDialog>
    );
};

EstimateBasisModal.displayName = 'EstimateBasisModal';
